/* ============================================================
   TV TYPES — the TV's declarative state.
   ------------------------------------------------------------
   The channel format itself is NOT defined here — it lives in the
   one canonical Zod schema (src/schema/channel.ts) and is re-
   exported below so the rest of the app keeps importing Channel
   from '@/tv/types' unchanged.
   ============================================================ */
export type { Channel, Program, ChannelOrigin, Breaks, BreakSlot, BreakItem, AdSlot, SelectMode } from '@/schema/channel'
/** @deprecated use Program */
export type { Program as ChannelProgram } from '@/schema/channel'
export type { Portion } from './portions'
import type { Portion } from './portions'

/** Hard on/off — the warm-up/collapse flashes are transient effects, not states. */
export type Power = 'off' | 'on'

/**
 * Whether the set may play with SOUND, as a single gate with three states:
 *   locked           — player not ready yet (boot snow), nothing to whack
 *   awaiting-gesture  — ready, holding on the bumper, waiting for the screen tap
 *   unlocked          — the whack (or a successful autoplay probe) landed; audio on
 * Browsers gate audible autoplay behind a user gesture the player iframe itself
 * receives, so we never boot straight to `unlocked`.
 */
export type AudioState = 'locked' | 'awaiting-gesture' | 'unlocked'

/**
 * What the tube is presenting right now. Mirrors the prototype's `#screen`
 * classes (is-off / is-snow / is-tuning / is-playing). The channel ident
 * bumper is a SEPARATE overlay (see `bumper` below), exactly as in the
 * prototype where the mode stays `tuning` while the bumper covers the snow.
 */
export type ScreenMode = 'off' | 'snow' | 'tuning' | 'playing'

/**
 * The playback transition phase — the explicit state of the cover→ready→reveal
 * lifecycle the reconciler drives (emitted as the `SetPlayback` effect; see
 * src/tv/engine/update.ts + TvProvider's runReconcile). Unlike `screen`
 * (what the tube *looks* like) this is *what the player is doing*:
 *   idle     — powered off / nothing playing
 *   tuning   — a channel change is underway (snow + channel ident)
 *   playing  — a video is live on screen
 *   bumping  — the ident is up while the next VIDEO buffers (muted, hidden);
 *              we cut to it only once it is genuinely ready (no double-play)
 *   ad       — an AD queue item is the content (its scene is shown; no video)
 * The UI and tests read this instead of inferring from `bumper`.
 */
export type PlaybackPhase = 'idle' | 'tuning' | 'playing' | 'bumping' | 'ad'

/**
 * The whole TV as one plain object. In the prototype this was smeared across
 * module-scoped vars (audioReady, awaitingWhack, pendingTune, gateOn, armed,
 * guideOpen, idx, sel, bootResume) PLUS body/#screen/.tv CSS classes. Here it
 * is a single value a reducer owns; components and effects read off it.
 */
export interface TvState {
  // --- persisted preferences (mirror of the old localStorage `state`) ---
  power: Power
  channelIndex: number // index into the channels[] lineup (was `idx` + channelNum)
  programIndex: number // video within the current channel
  volume: number // 0..100
  muted: boolean
  subtitles: boolean
  remotePos: { left: number; top: number } | null
  /** User scale for the on-screen remote (zoom factor; 1 = default). Persisted. */
  remoteScale: number

  // --- audio gate (was audioReady / awaitingWhack / gateOn) ---
  audio: AudioState

  // --- presentation (was the #screen / body classes) ---
  screen: ScreenMode

  /** Whether the channel ident bumper overlay is currently shown. */
  bumper: boolean

  /**
   * The playback transition phase (see PlaybackPhase). Source of truth for what
   * the player is doing; `bumper` is just the canvas-visibility derived signal.
   * Owned via SET_PLAYBACK, dispatched when the reconciler emits `SetPlayback`.
   */
  playback: PlaybackPhase

  // --- TV guide overlay: a cursor of (selected channel, selected program) ---
  guideOpen: boolean
  guideSel: number // selected channel row (the highlighted row)
  /**
   * Selected PROGRAM within the selected channel — the guide's program cursor.
   * Channel +/- moves `guideSel`; prev/next move THIS within the selected
   * channel's video list; Play/OK tunes to (guideSel, guideProgram). It previews
   * without touching playback. Reset to the channel's live index (if it's the
   * tuned channel) or 0 whenever the selected channel changes.
   */
  guideProgram: number
  /** Guide zoom level: index into GUIDE_ZOOMS (visible time span). */
  guideZoom: number
  /**
   * Guide search: `null` = not searching (normal grid); a string = search mode is active and this is
   * the live query. Reached by channel-up past the top row; results derive from `searchGuide`.
   */
  guideSearch: string | null
  /** Highlighted result index within the search dropdown (clamped to the result count at use). */
  guideSearchSel: number

  /**
   * Channel+program the set was watching before this session; cued (muted)
   * under the boot snow so the whack resumes exactly there. Cleared on unlock.
   * (Was the `bootResume` var.)
   */
  bootResume: { channelIndex: number; programIndex: number; live?: boolean } | null

  /**
   * Whether the tuned channel is following its LIVE broadcast. Set when going
   * live; cleared by any manual navigation. The player reconciles its position
   * to liveSlot(queue, now) while this is true, and the LIVE badge derives from
   * it — both the guide and the player read the same schedule, so they agree.
   */
  live: boolean

  /**
   * Whether the picture is ours-to-control RIGHT NOW: the foreground is genuinely playing, or we
   * intentionally paused it. False when YouTube has wrested control — a bot-check / sign-in
   * interstitial, a load error, or a stall. The screen's click-catch (pause-on-click) is enabled
   * ONLY while this is true; when it's false we let clicks fall through to the iframe so the viewer
   * can dismiss YouTube's overlay (e.g. sign in to clear an anti-bot challenge).
   */
  screenInteractive: boolean

  /**
   * YouTube is systemically blocking playback (error 150 on consecutive videos — typically an
   * IP-level bot flag), as opposed to a single dead video. While blocked the engine HALTS (stops
   * advancing/swapping), freezes the errored frame, and drops the click-catch so the viewer can
   * interact with YouTube's error/sign-in UI. Cleared the moment a video genuinely plays.
   */
  blocked: boolean

  /** Transient OSD overlays currently shown (channel badge / banner / volume). */
  osd: { channel: boolean; banner: boolean; volume: boolean }

  /**
   * Override content for the channel OSD. Used while DIALING a multi-digit
   * channel number (TV-style): the badge shows the digits accumulated so far,
   * and `confirm` flips it green for a beat right before the set tunes. Null =
   * show the current (or off-air) channel.
   */
  osdOverride: { num: number; label: string; confirm: boolean } | null

  /**
   * The set is tuned to an empty frequency — a dialed channel number with no
   * station in the lineup. Shows snow + "NO SIGNAL" for that number and STAYS
   * there (no snap-back to the nearest channel), which is what lets hidden /
   * not-yet-added channels exist. Null = tuned to a real lineup channel.
   */
  offAirNumber: number | null

  /** Whether the set is zoomed to fill the window (video-only view). */
  fullscreen: boolean

  /** Whether we're in TRUE (native) fullscreen — the screen element is promoted to the browser's
   *  top layer, so the remote must portal INTO it to be visible/summonable (it's outside otherwise). */
  nativeFs: boolean

  /**
   * Remote visibility while fullscreen. 'hidden' = docked off the bottom edge;
   * 'peek' = a sliver shows when the pointer nears the bottom; 'shown' = fully
   * in-window, auto-hiding after 5s idle. Ignored when not fullscreen.
   */
  remoteFs: RemoteFsState

  /** Touch devices only: is the remote slide-up sheet open? (Replaces the floating remote + the
   *  fullscreen peek/R-key dock on a phone — a handle summons it, swipe-down dismisses.) */
  remoteSheetOpen: boolean

  /** Touch: is the LCD touchscreen panel collapsed out of view? (Keeps the remote compact — and out
   *  of the guide's way in fullscreen.) Collapses vertically in portrait, horizontally in landscape. */
  remoteCollapsed: boolean

  /** Which screen the remote's LCD touchscreen is showing. */
  touchScreen: TouchScreen

  /** User display preferences set from the options screen (persisted). */
  settings: DisplaySettings

  /** Mischief gate shown before disabling ads from the options screen. */
  adBumperConfirm: AdBumperConfirmState | null

  /** Whether the optional local YouTube chrome-cleaner extension announced itself. */
  youtubeCleaner: boolean | null

  /** Whether the user has dismissed the "cleaner extension available" hint (persisted). */
  extensionHintDismissed: boolean

  /** Report-bug feedback on the options screen: idle, or whether the session id copied. */
  bugReport: false | 'copied' | 'nocopy'

  /**
   * The current channel's playable programs IN PLAY ORDER (natural or shuffled).
   * Single source of truth for both the DualPlayer (ids) and the LCD video list
   * (titles). A row's position here IS its queue index. Empty for noSignal.
   */
  queue: QueueItem[]

  /** The registry key of the interactive React component currently mounted AS content (e.g. HIDEO),
   *  or null. Owned by the STAGE's component presenter (it sets/clears this via SET_COMPONENT as part of
   *  its content transitions) — the in-tree mount stays here because the component needs TvContext. The
   *  Screen mounts channelComponent(activeComponent) when set. */
  activeComponent: string | null

  /** Whether the current channel's queue is shuffled. */
  shuffle: boolean

  /** Array of dialed channel numbers that are permanently unlocked (even if hidden). */
  unlockedSecrets: number[]
}

/**
 * One entry of the play-order queue. Either a VIDEO (a channel Program) or an
 * AD (a Three.js interstitial baked into the queue at the channel's ad cadence).
 * Ads are first-class items: the screen shows the ad scene as content (never a
 * video underneath), and "next" skips an ad like any other item. A row's
 * position here IS its queue index; `programIndex` indexes this mixed array.
 */
/** A queue item's role in the lineup — ORTHOGONAL to its render kind. A 'program' is a listed show you
 *  navigate to in the guide; a 'break' is an interstitial (injected ad) between programs. Default
 *  'program'. So a scene can be a program (the ad channel) and a video can be a break. */
export type ItemRole = 'program' | 'break'

/** A broadcast segment. `kind` is HOW it renders (video iframe / 3D scene / React component); `role` is
 *  WHAT it is in the lineup (program vs break). Behaviour per kind lives in src/tv/itemKinds.ts. */
export type QueueItem =
  | { kind: 'video'; role?: ItemRole; videoId: string; title: string; durationSeconds?: number; portions?: Portion[] }
  | { kind: 'scene'; role?: ItemRole; scene: string; params: Record<string, unknown>; title: string }
  // a custom interactive React component (registry key) run AS a segment — e.g. HIDEO. Its `durationSeconds`
  // is usually absent → Infinity ("always on"): one such program owns the whole non-wrapping timeline.
  | { kind: 'component'; role?: ItemRole; component: string; title: string; durationSeconds?: number }

/** Remote visibility levels while in fullscreen. */
export type RemoteFsState = 'hidden' | 'peek' | 'shown'

/** The touchscreen's current view: channel dial, video list, or options. */
export type TouchScreen = 'dial' | 'list' | 'options'

export interface AdBumperConfirmState {
  left: number
  top: number
  vx: number
  vy: number
  label: string
}

/**
 * Display toggles surfaced on the options screen. Each one removes a layer of
 * the skeuomorphic CRT treatment; `bareVideo` is the master "just the video".
 * Defaults keep the full retro look (everything on).
 */
export interface DisplaySettings {
  scanlines: boolean // the moving scanline overlay
  curvature: boolean // bulge + vignette + glare (the convex-glass fakery)
  ambient: boolean // room glow + floor pool + drifting dust
  crt: boolean // master switch for ALL crt-glass overlays
  bareVideo: boolean // strip everything but the YouTube picture
  adBumpers: boolean // inject the full-screen AD interstitials (NOT the program-ident overlays, which always show)
  overscan: boolean // let the bezel crop the picture edges (realistic CRT)
  trueFullscreen: boolean // in fullscreen, stretch the video edge-to-edge (no bezel)
}

export type DisplaySettingKey = keyof DisplaySettings

export const DEFAULT_SETTINGS: DisplaySettings = {
  scanlines: true,
  curvature: true,
  ambient: true,
  crt: true,
  bareVideo: false,
  adBumpers: true,
  overscan: true,
  trueFullscreen: false,
}

/** Just the slice we persist to localStorage. */
export interface PersistedTvState {
  power: Power
  channelNum: number
  programIndex: number
  volume: number
  muted: boolean
  subtitles: boolean
  remote: { left: number; top: number } | null
  remoteScale: number
  settings: DisplaySettings
  unlockedSecrets: number[]
}
